/**
 * ConfirmDeleteDialog — modal for a destructive delete that requires
 * the caller to re-enter their PIN. Sends the PIN as an `X-Confirm-Pin`
 * header (via the passed `onConfirm` callback).
 */
import { useEffect, useRef, useState } from "react";
import { X, ShieldAlert, Loader2, Eye, EyeOff } from "lucide-react";

export default function ConfirmDeleteDialog({
  title = "Delete statement?",
  description = "This will permanently remove the statement AND every transaction it produced. This cannot be undone.",
  onClose,
  onConfirm,
}) {
  const [pin, setPin] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    ref.current?.focus();
    const onKey = (e) => e.key === "Escape" && !busy && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, busy]);

  const submit = async (e) => {
    e.preventDefault();
    if (!pin.trim() || busy) return;
    setBusy(true);
    setError("");
    try {
      await onConfirm(pin.trim());
      // parent closes on success
    } catch (err) {
      const status = err?.response?.status;
      if (status === 403) setError("Incorrect PIN.");
      else if (status === 400) setError("PIN required.");
      else setError(err?.response?.data?.detail || "Delete failed.");
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[90] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center px-0 sm:px-4"
      role="dialog"
      aria-modal="true"
      onClick={() => !busy && onClose?.()}
      data-testid="ops-confirm-delete"
    >
      <div
        className="relative w-full sm:max-w-md bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl p-6 sm:p-7 pb-8"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          disabled={busy}
          className="absolute top-3 right-3 h-9 w-9 rounded-full text-slate-400 hover:text-brand-navy hover:bg-slate-100 flex items-center justify-center disabled:opacity-40"
          aria-label="Close"
          data-testid="ops-confirm-delete-close"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="h-11 w-11 rounded-full bg-red-50 flex items-center justify-center text-red-600">
          <ShieldAlert className="h-5 w-5" />
        </div>
        <h2 className="mt-3 text-xl font-bold font-heading text-brand-navy">{title}</h2>
        <p className="mt-1 text-sm text-slate-500 leading-relaxed">{description}</p>

        <form onSubmit={submit} className="mt-5 space-y-3">
          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              Enter your PIN to confirm
            </span>
            <div className="mt-1.5 relative">
              <input
                ref={ref}
                type={show ? "text" : "password"}
                inputMode="numeric"
                autoComplete="off"
                value={pin}
                onChange={(e) => setPin(e.target.value)}
                disabled={busy}
                className="w-full h-12 rounded-lg border border-slate-200 bg-white px-4 pr-11 text-lg tracking-widest text-brand-navy focus:outline-none focus:ring-2 focus:ring-red-500/40 focus:border-red-500 disabled:opacity-60"
                data-testid="ops-confirm-delete-pin"
              />
              <button
                type="button"
                onClick={() => setShow((v) => !v)}
                tabIndex={-1}
                className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 rounded-md text-slate-400 hover:text-brand-navy hover:bg-slate-100 flex items-center justify-center"
                aria-label={show ? "Hide PIN" : "Show PIN"}
              >
                {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </label>

          {error && (
            <p className="text-sm text-red-600" data-testid="ops-confirm-delete-error">{error}</p>
          )}

          <div className="pt-2 flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              className="px-4 h-11 rounded-full text-sm font-semibold text-slate-600 hover:bg-slate-100 transition disabled:opacity-50"
              data-testid="ops-confirm-delete-cancel"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!pin.trim() || busy}
              className="px-5 h-11 rounded-full bg-red-600 hover:bg-red-700 text-white font-semibold flex items-center gap-2 shadow-sm disabled:opacity-50 transition"
              data-testid="ops-confirm-delete-submit"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {busy ? "Deleting…" : "Delete permanently"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
